import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  CheckCircleIcon,
  FileTextIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { as400ImportIssueIsFatal, as400ImportIssueIsWarning } from '@erp/rpc';
import type { As400ImportFileReport, As400ImportIssue } from '@erp/rpc';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { As400ImportUpload } from '@/api/rpc/hooks/inventory-management/useAs400Import';
import type { DivisionOption } from './useDivisionSelection';

/**
 * What an AS/400 import read in each file, shared by the two dialogs that read
 * a batch — loading into an open review, and starting a new review from one.
 */

/** How many of a file's issues the list shows before it collapses the rest. */
const VISIBLE_ISSUES = 8;

/** How an issue reads: refused file, refused line, or loaded with a warning. */
function issueTone(issue: As400ImportIssue): string {
  if (as400ImportIssueIsFatal(issue.code)) return 'text-red-700';
  // A warning loads as written; it is said, not refused.
  if (as400ImportIssueIsWarning(issue.code)) return 'text-muted-foreground';
  return 'text-amber-800 dark:text-amber-300';
}

/** One issue line: the file, the line, the column and the reason. */
function IssueLine({ issue }: { issue: As400ImportIssue }) {
  return (
    <li className={issueTone(issue)}>
      <span className="font-mono text-[11px]">
        {issue.line === null ? (
          <T context="AS/400 import issue prefix when the problem is with the file as a whole rather than one line">
            file
          </T>
        ) : (
          <T context="AS/400 import issue prefix naming the file line the problem is on">
            line <Var name="line">{String(issue.line)}</Var>
          </T>
        )}
        {issue.column === null ? '' : ` · ${issue.column}`}
      </span>{' '}
      {issue.detail}
    </li>
  );
}

/** The one-line summary under a file's name: what kind of file, and its lines. */
function FileSummary({ report }: { report: As400ImportFileReport }) {
  if (report.group === null && report.mode !== null) {
    return (
      <T context="AS/400 import file summary for a location list file (ITMSL), which lists the locations the batch applies at">
        Location list · <Var name="lines">{String(report.lineCount)}</Var>{' '}
        location(s)
      </T>
    );
  }
  if (report.group === null) {
    return (
      <T context="AS/400 import file summary when the filename could not be read at all">
        Not a file we can place.
      </T>
    );
  }
  if (report.mode === 'ADD') {
    return (
      <T context="AS/400 import file summary for an adds (I1) file: the worksheet, its lines, and the new rows they author">
        <Var name="group">{report.group}</Var> ·{' '}
        <Var name="lines">{String(report.lineCount)}</Var> line(s) ·{' '}
        <Var name="rows">{String(report.newRowCount)}</Var> new row(s) ·{' '}
        <Var name="rejected">{String(report.rejectedLineCount)}</Var> refused
      </T>
    );
  }
  return (
    <T context="AS/400 import file summary: the worksheet, and how its lines were read">
      <Var name="group">{report.group}</Var> ·{' '}
      <Var name="lines">{String(report.lineCount)}</Var> line(s) ·{' '}
      <Var name="changed">{String(report.changedRowCount)}</Var> record(s) to
      change, <Var name="cells">{String(report.changedCellCount)}</Var> cell(s)
      · <Var name="unchanged">{String(report.noChangeLineCount)}</Var> unchanged
      · <Var name="rejected">{String(report.rejectedLineCount)}</Var> refused
    </T>
  );
}

/** What one file would do, or did. */
export function As400ImportFileReportRow({
  report,
}: {
  report: As400ImportFileReport;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded
    ? report.issues
    : report.issues.slice(0, VISIBLE_ISSUES);
  const hidden = report.issues.length - shown.length;
  const clean = report.issues.length === 0;

  return (
    <div className="space-y-1.5 rounded-md border p-2.5">
      <div className="flex items-start gap-2">
        {clean ? (
          <CheckCircleIcon
            className="mt-0.5 h-4 w-4 shrink-0 text-green-600"
            weight="fill"
          />
        ) : (
          <WarningIcon
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
            weight="fill"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate font-mono text-xs text-foreground">
            {report.fileName}
          </p>
          <p className="text-xs text-muted-foreground">
            <FileSummary report={report} />
          </p>
        </div>
        {report.applied ? (
          <span className="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800">
            <T context="badge on an AS/400 import file whose edits were staged">
              Loaded
            </T>
          </span>
        ) : undefined}
      </div>

      {report.issues.length > 0 ? (
        <ul className="space-y-1 pl-6 text-xs">
          {shown.map((issue) => (
            <IssueLine
              // An issue is its line, column, code and words: two that match
              // on all four say the same thing, and the planner reports once.
              key={`${issue.line}|${issue.column}|${issue.code}|${issue.detail}`}
              issue={issue}
            />
          ))}
          {hidden > 0 ? (
            <li>
              <button
                type="button"
                className="text-xs underline"
                onClick={() => setExpanded(true)}
              >
                <T context="button revealing the remaining problems found in an AS/400 import file">
                  Show <Var name="count">{String(hidden)}</Var> more
                </T>
              </button>
            </li>
          ) : undefined}
        </ul>
      ) : undefined}
    </div>
  );
}

/**
 * Below the file list: a refused file blocks everything, refused lines need an
 * explicit "skip them". Nothing when the batch read clean.
 */
export function As400ImportRefusalGate({
  hasFatalIssue,
  rejectedLineCount,
  skipRejectedLines,
  onSkipRejectedLinesChange,
}: {
  hasFatalIssue: boolean;
  rejectedLineCount: number;
  skipRejectedLines: boolean;
  onSkipRejectedLinesChange: (skip: boolean) => void;
}) {
  if (hasFatalIssue) {
    return (
      <div className="flex gap-1.5 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
        <WarningIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" weight="fill" />
        <p>
          <T context="AS/400 import warning when a whole file was refused and must be corrected">
            A file was refused outright — it is not the file its name says it
            is. Nothing can be loaded until that is fixed: these worksheets are
            positional, so one cell of the wrong width puts every value after it
            in the wrong field.
          </T>
        </p>
      </div>
    );
  }
  if (rejectedLineCount === 0) return null;
  return (
    <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
      <Checkbox
        checked={skipRejectedLines}
        onCheckedChange={(next) => onSkipRejectedLinesChange(next === true)}
        className="mt-0.5"
      />
      <span>
        <T context="AS/400 import checkbox asking permission to leave the lines that could not be placed out of the load">
          Load the rest and leave the{' '}
          <Var name="count">{String(rejectedLineCount)}</Var> refused line(s)
          out. Each one is a change someone asked for, so this is never assumed.
        </T>
      </span>
    </label>
  );
}

/** The chosen files, by name. */
export function As400ImportUploadList({
  uploads,
}: {
  uploads: readonly As400ImportUpload[];
}) {
  if (uploads.length === 0) return null;
  return (
    <ul className="space-y-0.5 text-xs text-muted-foreground">
      {uploads.map((upload) => (
        <li
          key={upload.fileName}
          className="flex items-center gap-1.5 font-mono"
        >
          <FileTextIcon className="h-3.5 w-3.5 shrink-0" />
          {upload.fileName}
        </li>
      ))}
    </ul>
  );
}

/** The one division an import reads its files against. */
export function As400ImportDivisionSelect({
  id,
  value,
  onValueChange,
  divisions,
}: {
  id: string;
  value: string;
  onValueChange: (dataSourceId: string) => void;
  divisions: readonly DivisionOption[];
}) {
  const gt = useGT();
  return (
    <div className="min-w-48 space-y-1.5">
      <Label htmlFor={id}>
        <T context="label for the division an AS/400 import loads into">
          Division
        </T>
      </Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id={id}>
          <SelectValue
            placeholder={gt('Pick a division', {
              context: 'placeholder in the AS/400 import division picker',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          {divisions.map((division) => (
            <SelectItem key={division.id} value={division.id}>
              {division.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
